/* eslint-disable @typescript-eslint/no-explicit-any */
import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, CUSTOM_ELEMENTS_SCHEMA, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CustomerService } from '@features/customers/services/customer.service';
import { EmailThread, EmailThreadType } from '@features/email/interfaces/email-thread.interface';
import { Email } from '@features/email/interfaces/email.interface';
import { EmailThreadsService } from '@features/email/services/email-threads.service';
import { Listing } from '@marketplace/api';
import { Store } from '@ngrx/store';
import { DownloadService } from '@shared/services/download/download.service';
import { EmailSettingsService } from '@shared/services/email/email-settings.service';
import { GmailService } from '@shared/services/integrations/google/gmail/gmail.service';
import { appQuery, fromAppActions } from '@shared/state';
import { fromApplicationActions } from '@shared/state/actions/application/application.actions';
import { applicationQuery } from '@shared/state/selectors/application/application.selectors';
import { catchError, finalize, first, firstValueFrom, map, Observable, of, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { CarouselLightboxModule } from '../../../../add-to-event-ui/carousel-lightboxes/carousel-lightbox.module';
import { NotificationsService, NotificationTypes } from '../../../../shared/services/notifications/notifications.service';
import { Enquiry } from '../../../enquiries/interfaces/enquiry.interface';
import { EnquiriesService } from '../../../enquiries/services/enquiries.service';
import { EmailHtmlViewerComponent } from '../../components/email-html-viewer.component';
import { NotLeadDialogComponent, NotLeadAction } from '../../components/not-lead-dialog/not-lead-dialog.component';

@Component({
  selector: 'email-component',
  templateUrl: './email.component.html',
  styleUrls: ['./email.component.scss'],
  imports: [CommonModule, RouterModule, EmailHtmlViewerComponent, CarouselLightboxModule, AsyncPipe, NotLeadDialogComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class EmailComponent implements OnInit, OnDestroy, OnDestroy {
  isLoading = false;
  error: string | null = null;
  message: Email | null = null;
  innerWidth: number;
  isMasquerading = false;
  linkedThread: EmailThread | null = null;
  listing: Listing | null = null;
  showNotLeadDialog = false;
  private destroy$ = new Subject<void>();
  lightboxObservable$: Observable<{ visible: boolean; lightboxImages: string[]; lightboxInitialIndex: number }>;

  constructor(
    private _route: ActivatedRoute,
    private _gmail: GmailService,
    private _enquiriesService: EnquiriesService,
    private _notificationsService: NotificationsService,
    private _downloadService: DownloadService,
    private _store: Store,
    private _customerService: CustomerService,
    private _router: Router,
    private _emailThreadsService: EmailThreadsService,
    private _emailSettingsService: EmailSettingsService
  ) {}

  @HostListener('window:resize')
  onResize() {
    this.innerWidth = window.innerWidth;
  }

  ngOnInit() {
    this._route.paramMap
      .pipe(
        map(pm => pm.get('id')),
        switchMap(id => {
          if (!id) {
            this.error = 'Missing email id in URL.';
            return of(null);
          }
          this.isLoading = true;
          this.error = null;
          this.message = null;

          return this._gmail.getMessage$(id).pipe(
            tap(msg => {
              if (msg?.labelIds?.includes('UNREAD')) {
                msg.labelIds = msg.labelIds.filter(l => l !== 'UNREAD');

                this._gmail
                  .markMessageRead$(msg.id)
                  .pipe(catchError(() => of(null)))
                  .subscribe();
              }
            }),
            catchError(err => {
              this.error = 'Failed to load email.';
              console.error(err);
              return of(null);
            }),
            finalize(() => (this.isLoading = false))
          );
        })
      )
      .subscribe(msg => {
        if (msg) {
          this.message = msg;

          this.message.attachments.filter(a => a.mimeType?.startsWith('image/')).forEach(att => this.loadAttachmentPreview(msg.id, att));

          this.loadLinkedThread(msg.threadId, msg.id);
        }
      });

    this.lightboxObservable$ = this._store.select(applicationQuery.getLightboxState);

    this._store
      .select(appQuery.getListing)
      .pipe(takeUntil(this.destroy$))
      .subscribe(listing => {
        this.listing = listing ?? null;
      });
  }

  formatSize(bytes: number): string {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  }

  loadAttachmentPreview(messageId: string, att: any) {
    this._gmail
      .getAttachment$(messageId, att)
      .pipe(first())
      .subscribe((res: any) => {
        att.previewUrl = URL.createObjectURL(res);
      });
  }

  openImageLightbox(message: any, attachmentId: number): void {
    const images = message.attachments?.filter(a => a.mimeType?.startsWith('image/'));
    const imageIds = images.map(a => a.attachmentId);
    const imageIndex = imageIds.indexOf(attachmentId);
    const imageUrls = images?.map(img => img.previewUrl);
    this._store.dispatch(
      new fromApplicationActions.SetLightbox({
        images: imageUrls,
        index: imageIndex
      })
    );
  }

  navigateToCustomerEnquiry(enquiry?: Enquiry, uuid?: string) {
    const supplierUuid = this._route.snapshot.paramMap.get('listingUuid');

    if (!supplierUuid) return;

    const route = uuid ? ['/su', supplierUuid, 'enquiries', 'add', uuid] : ['/su', supplierUuid, 'enquiries', 'add'];

    this._router.navigate(route, {
      queryParams: enquiry
        ? {
            generatedEnquiry: true,
            threadId: this.message?.threadId ?? null,
            messageId: this.message?.id ?? null,
            senderEmail: this.message?.from?.email ?? null,
            recordGenerator: 'manual_ai_from_email_parser'
          }
        : {},
      replaceUrl: true
    });
  }

  private loadLinkedThread(threadId: string, messageId?: string): void {
    if (!threadId) return;

    this._emailThreadsService
      .getEmailThreads$({ threadId, listingUuid: this.listing?.uuid })
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          const records = result?.data ?? [];
          const messageMatch = messageId ? records.find(t => t.messageId === messageId) : null;
          if (messageId) {
            // Aggregator context: only apply thread-level records (no messageId) as fallback.
            // Never show a record that belongs to a different message in the same thread.
            const threadLevelFallback =
              records.find(t => !t.messageId && t.type === EmailThreadType.Enquiry) ??
              records.find(t => !t.messageId) ??
              null;
            this.linkedThread = messageMatch ?? threadLevelFallback;
          } else {
            this.linkedThread = records.find(t => t.type === EmailThreadType.Enquiry) ?? records[0] ?? null;
          }
        },
        error: err => console.error('Failed to load email thread', err)
      });
  }

  get hasLinkedEnquiry(): boolean {
    return this.linkedThread?.type === EmailThreadType.Enquiry;
  }

  get isNotLead(): boolean {
    return this.linkedThread?.type === EmailThreadType.NotLead;
  }

  get isAutoNotALead(): boolean {
    return this.linkedThread?.type === EmailThreadType.AutoNotALead;
  }

  get isNotA(): boolean {
    return this.linkedThread?.type === EmailThreadType.NotLead;
  }

  get linkedEnquiryUuid(): string | null {
    return this.linkedThread?.enquiryUuid ?? null;
  }

  navigateToLinkedEnquiry(): void {
    const supplierUuid = this._route.snapshot.paramMap.get('listingUuid');
    if (!supplierUuid || !this.linkedEnquiryUuid) return;
    this._router.navigate(['/su', supplierUuid, 'enquiries', 'enquiry', this.linkedEnquiryUuid]);
  }

  /**
   * Sends the email to the LLM for parsing into a customer enquiry.
   */
  convertEnquiry() {
    if (!this.message || !this.message?.textBody) {
      console.error('Aborted when trying to create an enquiry from an email where the email does not exist');
      return;
    }
    this.isLoading = true;
    const email = this.message.from.email ?? '';
    const body = this.message.textBody;
    const html = this.message.htmlBody;
    const name = this.message.from?.name;

    const emailText = [
      this.message.date ? `Email Sent On: ${this.message.date}` : null,
      email ? `Email: ${email}` : null,
      name ? `Alias: ${name}` : null,
      this.message.subject ? `Subject: ${this.message.subject}` : null,
      body ? `Body: ${body}` : null
    ]
      .filter(Boolean)
      .join(' ');
    this._enquiriesService
      .getEnquiryFromDetails(emailText, email, body, html)
      .pipe(
        first(),
        switchMap((enquiry: Enquiry) => {
          const customerEmail = enquiry.email ?? email ?? '';
          return this._customerService.getCustomers({ limit: 200 }).pipe(
            take(1),
            catchError(() => of({ data: [] })),
            map(customers => {
              const customer = customers.data.find(c => c.email?.toLowerCase() === customerEmail.toLowerCase());

              return { enquiry, customer };
            })
          );
        }),
        finalize(() => {
          this.isLoading = false;
        })
      )
      .subscribe({
        next: ({ enquiry, customer }) => {
          this._notificationsService.openSnackBar({
            message: 'Successfully extracted enquiry details from email.',
            action: 'Close',
            type: NotificationTypes.SUCCESS
          });

          if (customer) {
            this.navigateToCustomerEnquiry(enquiry, customer.uuid);
          } else {
            this.navigateToCustomerEnquiry(enquiry);
          }
        },
        error: error => {
          console.error(error);
          this._notificationsService.openSnackBar({
            message: error.message || 'Error extracting enquiry details from email. Please try again.',
            action: 'Close',
            type: NotificationTypes.ERROR
          });
        }
      });
  }

  get id(): string {
    return this.message?.id;
  }

  downloadAttachment(messageId: string, attachmentId: string) {
    const file = this.message.attachments.find(a => a.attachmentId === attachmentId);
    if (!file) return;

    this._gmail.getAttachment$(messageId, file).subscribe(blob => {
      this._downloadService.downloadBlob(blob, file.filename);
    });
  }

  openNotLeadDialog(): void {
    this.showNotLeadDialog = true;
  }

  onNotLeadAction(action: NotLeadAction): void {
    if (action === 'thread') this._excludeThread();
    else if (action === 'address') this._excludeAddress();
    else if (action === 'domain') this._excludeDomain();
  }

  private async _excludeThread(): Promise<void> {
    if (!this.message?.threadId) return;
    const supplierUuid = this._route.snapshot.paramMap.get('listingUuid');
    try {
      await firstValueFrom(
        this._emailThreadsService.createEmailThread$({
          type: EmailThreadType.NotLead,
          threadId: this.message.threadId,
          messageId: this.message.id ?? undefined,
          senderEmail: this.message.from?.email ?? undefined,
          listingUuid: supplierUuid ?? undefined,
          emailProvider: 'Gmail',
          updated: new Date().toISOString()
        })
      );
      this.loadLinkedThread(this.message.threadId, this.message.id);
      this._notificationsService.openSnackBar({
        message: 'Email thread excluded from Leads inbox.',
        action: 'Close',
        type: NotificationTypes.SUCCESS
      });
    } catch {
      this._notificationsService.openSnackBar({
        message: 'Failed to exclude email thread. Please try again.',
        action: 'Close',
        type: NotificationTypes.ERROR
      });
    }
  }

  private async _excludeAddress(): Promise<void> {
    const address = this.message?.from?.email;
    if (!address || !this.listing) return;
    try {
      await firstValueFrom(this._emailSettingsService.addLeadExclusionEntry$(address, this.listing));
      this._store.dispatch(new fromAppActions.GetListing());
      this._notificationsService.openSnackBar({
        message: `Emails from ${address} excluded from Leads inbox.`,
        action: 'Close',
        type: NotificationTypes.SUCCESS
      });
    } catch {
      this._notificationsService.openSnackBar({
        message: 'Failed to exclude email address. Please try again.',
        action: 'Close',
        type: NotificationTypes.ERROR
      });
    }
  }

  private async _excludeDomain(): Promise<void> {
    const domain = this.message?.from?.email?.split('@')[1];
    if (!domain || !this.listing) return;
    try {
      await firstValueFrom(this._emailSettingsService.addLeadExclusionEntry$(domain, this.listing));
      this._store.dispatch(new fromAppActions.GetListing());
      this._notificationsService.openSnackBar({
        message: `Emails from ${domain} excluded from Leads inbox.`,
        action: 'Close',
        type: NotificationTypes.SUCCESS
      });
    } catch {
      this._notificationsService.openSnackBar({
        message: 'Failed to exclude domain. Please try again.',
        action: 'Close',
        type: NotificationTypes.ERROR
      });
    }
  }

  ngOnDestroy() {
    this._store.dispatch(new fromApplicationActions.RemoveLightbox());
    this.destroy$.next();
    this.destroy$.complete();
  }

  get nonInlineAttachments() {
    return this.message?.attachments?.filter(a => !a.isInlineImage) ?? [];
  }
}
